/*
 * This file contains the floorplan list component.
 */

import styles from "../floorplans.module.scss";
import FloorPlanListCard from "./floorplanListCard";
import Pagination from "../../Pagination/pagination";
import { formatResultCount, getBreadcrumbUrl, getAvailabilityDate } from "../../../utils/util";
import FloorplanSkelton from "./floorplanSkelton/floorplanSkelton";
import defaultImage from "../../../assets/images/no-image.png";
import FloorplanImageSlider from "./floorplanImageSlider";
import CommunityCostGuide from "./communityCostGuide";

export default function FloorplanList({
  data,
  typeListing,
  page,
  setPage,
  loading,
  contactLink,
  params,
  moveInStartDate,
  setIsUserInteraction,
  setIsPaginationInteraction,
  amenities,
  unitAmenities,
}) {
  let dataToDisplay = data?.fp || [];

  // This function handles the page change event.
  // It updates the page state with the new page number.
  const handlePageChange = (page) => {
    setIsPaginationInteraction(true);
    setPage(parseInt(page));
    setIsUserInteraction(true);
  };

  // This conditional sets the dataToDisplay to the units of the first floorplan if typeListing is false and there are floorplans available.
  if (!typeListing && data?.fp?.length > 0) {
    dataToDisplay = data?.fp[0]?.units || [];
  }

  // Check if any floorplan has virtual tour URL
  const hasAnyVirtualTour = typeListing
    ? dataToDisplay?.some(item => item?.virtual_tour_url)
    : dataToDisplay?.some(item => item?.virtual_tour_url);

  // Handle image error
  const handleImageError = (e) => {
    e.target.onerror = null;
    e.target.src = defaultImage;
  };

  if (loading) {
    return (
      <div className={styles.FloorplanList}>
        <FloorplanSkelton activeTab="list" typeListing={typeListing} />
      </div>
    );
  }

  const fpData = data?.fp?.[0];
  const hasFloorplanDetail = !typeListing && fpData?.floorplan_id;

  // This conditional renders an error message if there are no floorplan results.
  // Detail pages can have a floorplan with no available units for the selected move-in date.
  if (data && data.total === 0 && !hasFloorplanDetail) {
    return (
      <div className={styles.FloorplanList}>
        <div className={styles.errorContainer}>
          <div>
            <h3>No results found</h3>
            <p>
              {data?.config?.no_result_message?.split("contact [url]")[0]}
              <a href={data?.config?.contact_page_url}>contact</a>
              {data?.config?.no_result_message?.split("contact [url]")[1]}
            </p>
          </div>
        </div>
        <CommunityCostGuide data={data} />
      </div>
    );
  }

  // Show skeleton if data is not yet loaded (during navigation)
  if (!data) {
    return (
      <div className={styles.FloorplanList}>
        <FloorplanSkelton activeTab="list" typeListing={typeListing} />
      </div>
    );
  }

  return (
    <div className={styles.FloorplanList}>
      {hasFloorplanDetail && (
        <>
          <p className={styles.InnerNav}>
            <a href={getBreadcrumbUrl(params)}>Floor Plan</a>&nbsp;/&nbsp;
            <a href={`/floor-plans/${fpData?.floorplan_name}/${fpData?.floorplan_id}${params ? `/?${params}` : ""}`}>
              {fpData?.floorplan_name}
            </a>
          </p>
          <h2 className={styles.FloorplanName}>
            {fpData?.floorplan_name}
          </h2>
          <ul className={styles.FloorplanChips}>
            <li>
              {fpData?.bed_count === "0" ? "Studio" : `${fpData?.bed_count} Bedroom`}
            </li>
            <li>{parseFloat(fpData?.bath_count)} Bath</li>
          </ul>

          {/* Floorplan Hero Section */}
          <div className={`${styles.FloorplanHero} ${!(amenities && amenities.length > 0 && amenities.some(amenity => amenity?.amenities_items && amenity.amenities_items.length > 0)) ? styles.NoAmenities : ''}`}>
            <div className={styles.FloorplanImageSection}>
              <div className={styles.AvailabilityBadge}>
                {getAvailabilityDate(fpData?.min_availability_date, fpData?.status) !== "" &&
                  `Available ${getAvailabilityDate(fpData?.min_availability_date, fpData?.status)}`
                }
              </div>
              {fpData?.images && Array.isArray(fpData.images) && fpData.images.length > 1 ? (
                <FloorplanImageSlider
                  images={fpData.images}
                  altText={fpData?.floorplan_name}
                  onImageError={handleImageError}
                  fallbackImage={fpData?.floorplan_image}
                />
              ) : (
                <div className={styles.FloorplanImageWrapper}>
                  <img
                    src={fpData?.images?.[0]?.image_url || fpData?.floorplan_image || defaultImage}
                    alt={fpData?.floorplan_name}
                    onError={handleImageError}
                  />
                </div>
              )}
              <p className={styles.ImageDisclaimer}>
                Floor plans are for illustrative purposes only. Actual layout and square footage may vary.
              </p>
            </div>

            {amenities && amenities.length > 0 && amenities.some(amenity =>
              amenity?.amenities_items && amenity.amenities_items.length > 0
            ) && (
                <div className={styles.AmenitiesSection}>
                  <h3>Featured Amenities</h3>
                  <ul>
                    {amenities?.map((amenity, index) =>
                      amenity?.amenities_items
                      ?.split(/,\s*\n|\n/)
                        .filter((item) => item.trim() !== "")
                        .map((item, itemIndex) => (
                          <li key={`${index}-${itemIndex}`}>
                            <svg width="6" height="6" viewBox="0 0 6 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <circle cx="3" cy="3" r="2" stroke="#AD8E6F" />
                            </svg>
                            <span>{item.trim()}</span>
                          </li>
                        ))
                    )}
                  </ul>
                  <span className={styles.AmenitiesDisclaimer}>
                    *Some features are not available in all suites.
                  </span>
                </div>
              )}
          </div>
        </>
      )}

      {data?.total > 0 ? (
        <>
          <h3 className={styles.AvailabilitySubtitle}>Availability</h3>
          <p className={styles.ResultsCount}>
            Showing {formatResultCount(data?.total)} results
          </p>

          <div className={styles.FloorplanListContainer}>
            {dataToDisplay?.map((item, index) => (
              <FloorPlanListCard
                typeListing={typeListing}
                key={`${item?.id}${index}`}
                data={item}
                fpData={typeListing ? data?.fp[index] : data?.fp[0]}
                specials={data?.specials}
                contactLink={contactLink}
                params={params}
                moveInStartDate={moveInStartDate}
                hasAnyVirtualTour={hasAnyVirtualTour}
                unitAmenities={unitAmenities?.[item?.marketing_name]}
                engrainFeeCalculator={data?.engrain_fee_calculator}
                feeCalculatorUrl={data?.config?.sightmap_url}
              />
            ))}
          </div>
        </>
      ) : hasFloorplanDetail ? (
        <div className={styles.errorContainer}>
          <div>
            <h3>No units available</h3>
            <p>
              There are no units available for the selected move-in date.{" "}
              <a href={contactLink}>Contact us</a> for more details.
            </p>
          </div>
        </div>
      ) : (
        <div className={styles.FloorplanList}>
          <div className={styles.errorContainer}>
            <div>
              <h3>No results found</h3>
              <p>
                {data?.config?.no_result_message?.split("contact [url]")[0]}
                <a href={data?.config?.contact_page_url}>contact</a>
                {data?.config?.no_result_message?.split("contact [url]")[1]}
              </p>
            </div>
          </div>
        </div>
      )}
      {/* <div className={styles.footerDisclaimer}>
        Floor plans are artist's rendering. All dimensions are approximate.
        Actual product and specifications may vary in dimension or detail. Not
        all features are available in every apartment. Prices and availability
        are subject to change and may not be reflective of the selected move-in
        date. For questions, please contact our leasing team.
      </div> */}
      {data?.total > 0 && (
        <Pagination
          currentPage={page}
          totalPages={Math.ceil(data?.total / data?.perpage)}
          onPageChange={handlePageChange}
        />
      )}

      {/* Engrain Fee Calculator Disclaimers */}
      {data?.engrain_fee_calculator?.enabled === "1" && (
        <div className={styles.FeeCalculatorDisclaimers}>

          {data?.engrain_fee_calculator?.enabled === "1" && data?.engrain_fee_calculator?.long_disclaimer && (
            <div
              className={styles.LongDisclaimer}
            >
              <p dangerouslySetInnerHTML={{ __html: data?.engrain_fee_calculator?.long_disclaimer }} />
            </div>
          )}
        </div>
      )}

      <CommunityCostGuide data={data} />
    </div>
  );
}
